<template>
  <div
    :class="classes"
    :data-project-id="projectId"
    :data-project-title="project ? project.title : null"
  >
    <ewr-modal
      class="alert"
      v-if="popupText"
      @close="popupText = false"
      :header="false"
    >
      <div class="alert-text">{{ popupText }}</div>
      <template slot="footer">
        <ewr-button
          ref="confirmButton"
          :text="'OK'"
          @click="popupText = null"
        />
      </template>
    </ewr-modal>

    <div v-if="project">
      <!-- DEBUG: field names -->
      <!-- <div>{{ registrationFieldNames }}</div> -->
      <!-- <pre>{{ registrationFields }}</pre> -->

      <form
        :class="formClasses"
        autocomplete="off"
        @submit.prevent="onFormSubmit"
      >
        <div class="form-fields">
          <template v-for="field in dynamicallyRenderedRegistrationFields">
            <!-- AGENT -->
            <template v-if="field.name === 'isAgent'">
              <!-- IS AGENT -->
              <ewr-form-field
                :key="`${field.name}--isAgent`"
                infield-top-aligned
                :label="fieldLabel('isAgent', 'Are you an agent?')"
                :invalid="errors.has('isAgent')"
                :disclaimer="fieldDisclaimer('isAgent')"
                :class="fieldClasses('isAgent')"
              >
                <ewr-radio-group inline>
                  <li>
                    <input
                      id="radio-ef8399ca"
                      v-model="form.isAgent"
                      type="radio"
                      name="isAgent"
                      :value="true"
                    />
                    <label for="radio-ef8399ca">Yes</label>
                  </li>
                  <li>
                    <input
                      id="radio-8514af96"
                      v-model="form.isAgent"
                      type="radio"
                      name="isAgent"
                      :value="false"
                    />
                    <label for="radio-8514af96">No</label>
                  </li>
                </ewr-radio-group>
              </ewr-form-field>

              <!-- IS REPRESENTED -->
              <ewr-form-field
                :key="`${field.name}--represented`"
                v-if="form.isAgent === false"
                infield-top-aligned
                :label="
                  fieldLabel(
                    'isRepresented',
                    'Are you represented by an agent?'
                  )
                "
                :invalid="errors.has('isRepresented')"
                :disclaimer="fieldDisclaimer('isRepresented')"
                :class="fieldClasses('isRepresented')"
              >
                <ewr-radio-group inline>
                  <li>
                    <input
                      id="isRepresented[true]"
                      v-model="form.isRepresented"
                      v-validate="
                        `${isFieldRequired('isRepresented') ? 'required' : ''}`
                      "
                      type="radio"
                      name="isRepresented"
                      :value="true"
                    />
                    <label for="isRepresented[true]">Yes</label>
                  </li>
                  <li>
                    <input
                      id="isRepresented[false]"
                      v-model="form.isRepresented"
                      type="radio"
                      name="isRepresented"
                      :value="false"
                    />
                    <label for="isRepresented[false]">No</label>
                  </li>
                </ewr-radio-group>
              </ewr-form-field>

              <!-- AGENT INFO -->
              <template v-if="form.isRepresented">
                <!-- AGENT: NAME -->
                <ewr-form-field
                  :key="`field--${field.name}`"
                  infield-top-aligned
                  :label="fieldLabel('agent[name]', 'Agent name *')"
                  :invalid="errors.has('agent[name]')"
                  :disclaimer="fieldDisclaimer('agent[name]')"
                  :class="fieldClasses('agent[name]')"
                >
                  <input
                    v-model="form['agent[name]']"
                    v-validate="'required'"
                    type="text"
                    name="agent[name]"
                  />
                </ewr-form-field>

                <!-- AGENT: COMPANY -->
                <ewr-form-field
                  :key="`field--${field.name}`"
                  infield-top-aligned
                  :label="
                    fieldLabel('agent[company]', 'Agent company / brokerage')
                  "
                  :invalid="errors.has('agent[company]')"
                  :disclaimer="fieldDisclaimer('agent[company]')"
                  :class="fieldClasses('agent[company]')"
                >
                  <input
                    v-model="form['agent[company]']"
                    v-validate="
                      `${isFieldRequired('agent[company]') ? 'required' : ''}`
                    "
                    type="text"
                    name="agent[company]"
                  />
                </ewr-form-field>

                <!-- AGENT: EMAIL -->
                <ewr-form-field
                  :key="`field--${field.name}`"
                  infield-top-aligned
                  :label="fieldLabel('agent[email]', 'Agent email')"
                  :invalid="errors.has('agent[email]')"
                  :disclaimer="fieldDisclaimer('agent[email]')"
                  :class="fieldClasses('agent[email]')"
                >
                  <input
                    v-model="form['agent[email]']"
                    v-validate="
                      `${
                        isFieldRequired('agent[email]') ? 'required' : ''
                      }|email`
                    "
                    type="text"
                    name="agent[email]"
                  />
                </ewr-form-field>

                <!-- AGENT: PHONE -->
                <ewr-form-field
                  :key="`field--${field.name}`"
                  infield-top-aligned
                  :label="fieldLabel('agent[phone]', 'Agent phone number')"
                  :invalid="errors.has('agent[phone]')"
                  :disclaimer="fieldDisclaimer('agent[phone]')"
                  :class="fieldClasses('agent[phone]')"
                >
                  <ewr-phone-input
                    v-model="form['agent[phone]']"
                    v-validate="
                      `${isFieldRequired('agent[phone]') ? 'required' : ''}`
                    "
                    data-vv-name="agent[phone]"
                  />
                </ewr-form-field>
              </template>
            </template>

            <!-- FOLLOW FIELD (inline checkbox) -->
            <ewr-form-field
              v-else-if="field.name === 'follow'"
              :key="`field--${field.name}--follow`"
              inline
              :class="fieldClasses(field.name)"
              :invalid="errors.has(field.name)"
              :disclaimer="fieldDisclaimer(field.name)"
            >
              <input
                id="checkbox-84f64015"
                v-model="form.follow"
                type="checkbox"
                name="follow"
                :value="true"
              />
              <label for="checkbox-84f64015">{{
                fieldLabel('follow', 'Stay informed?')
              }}</label>
            </ewr-form-field>

            <!-- COMMENTS / MESSAGE (textarea) -->
            <ewr-form-field
              v-else-if="field.name === 'text'"
              :key="`field--${field.name}--text`"
              infield-top-aligned
              :label="fieldLabel('text', 'Message')"
              :class="['comments-field'].concat(fieldClasses('text'))"
              :invalid="errors.has('text')"
              :disclaimer="fieldDisclaimer('text')"
            >
              <!-- <ewr-content-editable
              v-model="form.text"
              v-validate="`${isFieldRequired('text') ? 'required' : ''}`"
              name="text"
            /> -->
              <ewr-textarea
                v-model="form.text"
                v-validate="`${isFieldRequired('text') ? 'required' : ''}`"
                name="text"
              />
            </ewr-form-field>

            <!-- CUSTOM FIELDS -->
            <!-- (Select dropdown if options. Otherwise, a text field) -->
            <ewr-form-field
              v-else-if="field.name.startsWith('customFields[')"
              :key="`custom-field--${field.name}`"
              infield-top-aligned
              :label="`${field.label} ${
                isFieldRequired(field.name) ? '*' : ''
              }`"
              :invalid="errors.has(field.name)"
              :disclaimer="fieldDisclaimer(field.name)"
              :class="fieldClasses(field.name)"
            >
              <template v-if="fieldOptions(field.name).length > 0">
                <ewr-select
                  v-model="form[field.name]"
                  v-validate="
                    `${isFieldRequired(field.name) ? 'required' : ''}`
                  "
                  :data="fieldOptions(field.name)"
                  :name="field.name"
                  label-prop="label"
                  value-prop="value"
                />
              </template>

              <input
                v-else
                type="text"
                v-model="form[field.name]"
                :name="field.name"
                v-validate="`${isFieldRequired(field.name) ? 'required' : ''}`"
              />
            </ewr-form-field>

            <!-- METADATA -->
            <!-- (always a text field) -->
            <ewr-form-field
              v-else-if="field.name.startsWith('metadata[')"
              :key="`metadata-field--${field.name}`"
              infield-top-aligned
              :label="`${field.label} ${
                isFieldRequired(field.name) ? '*' : ''
              }`"
              :invalid="errors.has(field.name)"
              :disclaimer="fieldDisclaimer(field.name)"
              :class="fieldClasses(field.name)"
            >
              <input
                type="text"
                v-model="form[field.name]"
                :name="field.name"
                v-validate="`${isFieldRequired(field.name) ? 'required' : ''}`"
              />
            </ewr-form-field>

            <!-- NORMAL FIELDS -->
            <ewr-form-field
              v-else
              :key="field.name"
              infield-top-aligned
              :label="fieldLabel(field.name)"
              :class="fieldClasses(field.name)"
              :invalid="errors.has(field.name)"
              :disclaimer="fieldDisclaimer(field.name)"
            >
              <!-- PHONE -->
              <ewr-phone-input
                v-if="['phone'].includes(field.name)"
                v-model="form[field.name]"
                v-validate="`${isFieldRequired(field.name) ? 'required' : ''}`"
                :data-vv-name="field.name"
              />

              <!-- SELECT -->
              <ewr-select
                v-else-if="
                  [
                    'preferredContactMethod',
                    'source',
                    'address[countryCode]',
                    'preferences[bedrooms]',
                    'preferences[collectionIds]',
                    'preferences[floorPlanIds]',
                    'preferences[maxPrice]',
                    'preferences[occupancyType]',
                    'preferences[timeframe]'
                  ].includes(field.name) ||
                  (field.name.startsWith('address[administrativeArea]') &&
                    isDomestic)
                "
                v-model="form[field.name]"
                v-validate="`${isFieldRequired(field.name) ? 'required' : ''}`"
                :data="fieldOptions(field.name)"
                :name="field.name"
                label-prop="label"
                value-prop="value"
                :multiple="fieldAllowsMultiple(field.name)"
              />

              <!-- DEFAULT (text input field) -->
              <input
                v-else
                v-model="form[field.name]"
                v-validate="'required'"
                type="text"
                :name="field.name"
              />
            </ewr-form-field>
          </template>
        </div>

        <div class="form-actions">
          <ewr-button
            ref="submitButton"
            :disabled="processing"
            type="submit"
            :text="submitButtonLabel"
          />
        </div>
      </form>
    </div>
    <div v-else-if="error" class="error">{{ error }}</div>
    <div v-else-if="preloader" class="preloader">loading...</div>
  </div>
</template>

<script>
/**
 * ProjectContactForm
 *
 * Emits `created`, `mounted`, and `submit` events
 * Both `this.$emit` and `this.$root.$emit` are emitted.
 * See main-lib.js for rationale.
 *
 * Assigns utm (source, medium, campaign) to `form.utm.*`,
 * as well as `form.metadata.utm_*`.
 */

import _find from 'lodash/find'
import _flow from 'lodash/flow'
import _map from 'lodash/map'
import _omit from 'lodash/omit'
import _reduce from 'lodash/reduce'
import _some from 'lodash/some'

import gql from 'graphql-tag'
import WcBase from '../mixins/wc-base'

import StatesDataSet from '@/assets/datasets/states'
import CountriesDataSet from '@/assets/datasets/countries'

const qs = require('qs')
const globalConfig = window.Edgewise?.prototype || {}

const DEFAULT_FORM_FIELDS = [
  {
    name: 'name',
    label: 'Name',
    required: true
  },
  {
    name: 'email',
    label: 'Email address',
    required: true
  },
  {
    name: 'text',
    label: 'Message',
    required: false
  }
]

const DEFAULT_CONTACT_METHOD_OPTIONS = [
  { label: 'Email', value: 'EMAIL' },
  { label: 'Text', value: 'SMS' },
  { label: 'Call', value: 'VOICE' }
]

export default {
  $_veeValidate: { validator: 'new' },
  mixins: [WcBase],

  props: {
    projectId: {
      type: [Number, String],
      required: true,
      default: () => globalConfig.projectId || null
    },
    theme: {
      type: String,
      default: () => globalConfig.theme || 'vanilla'
    },
    formId: {
      type: String,
      required: false
    },
    preloader: {
      type: Boolean,
      default: true
    },
    submitButtonLabel: {
      type: String,
      default: 'Request Info'
    },
    debug: {
      type: Boolean,
      default: () => globalConfig.debug || false
    },
    defaults: {
      type: String,
      default: '{}'
    },
    columns: {
      type: [Number, String],
      default: 1
    },
    confirmationDisabled: {
      type: Boolean,
      default: false
    },
    recaptchaSiteKey: {
      type: String,
      default: null
    },
    recaptchaThreshold: {
      type: Number,
      default: 0.5
    }
  },

  data: () => ({
    states: StatesDataSet,
    countries: CountriesDataSet,
    mounted: false,
    project: null,
    form: { origin: 'PROJECT_WEBSITE', isRepresented: null },
    popupText: null,
    processing: false,
    error: null,
    urlParams: null,
    defaultOccupancyTypePreferenceOptions: [
      { label: 'Primary Residence', value: 'PRIMARY_RESIDENCE' },
      { label: 'Secondary Residence', value: 'SECONDARY_RESIDENCE' },
      { label: 'Investment Property', value: 'INVESTMENT_PROPERTY' },
      { label: 'Other', value: null }
    ],
    defaultTimeframePreferenceOptions: [
      { label: 'Immediate', value: 'IMMEDIATE' },
      { label: 'Less than 3 months', value: 'LTE3' },
      { label: '3-6 months', value: 'GTE3_LTE6' },
      { label: '6-12 months', value: 'GTE6_LTE12' },
      { label: 'More than 12 months', value: 'GTE12' }
    ],
    defaultBedroomPreferenceOptions: [
      { label: '1 Bedroom', value: 'BD_1' },
      { label: '2 Bedrooms', value: 'BD_2' },
      { label: '3 Bedrooms', value: 'BD_3' },
      { label: '4 Bedrooms', value: 'BD_4' },
      { label: '5 Bedrooms', value: 'BD_5' },
      { label: 'Penthouse', value: 'PENTHOUSE' },
      { label: 'Studio', value: 'STUDIO' }
    ],
    defaultMaxPricePreferenceOptions: [
      { label: 'Less than $500K', value: 500000 },
      { label: 'Less than $750K', value: 750000 },
      { label: 'Less than $1M', value: 1000000 },
      { label: 'Less than $2M', value: 2000000 },
      { label: '$2M+', value: null }
    ],
    recaptchaReady: false
  }),

  computed: {
    classes() {
      return [
        'ewc',
        'ewc-project-contact-form',
        `ewc-project-contact-form--theme-${this.theme}`,
        `ewc-project-contact-form--columns-${this.columns}`,
        {
          'ewc-project-contact-form--mounted': this.mounted
        }
      ]
    },

    formClasses() {
      return ['form'].concat(
        Object.keys(this.form).reduce((acc, fieldName) => {
          // For boolean fields only, we add a class with a sanitized field name
          // and the value (true/false) to the form element.

          const value = this.form[fieldName]
          const sanitizedFieldName = this.sanitizeFieldName(fieldName)

          return [true, false].includes(value)
            ? [...acc, `form--${sanitizedFieldName}-${this.form[fieldName]}`]
            : acc
        }, [])
      )
    },

    isValid() {
      return !_find(this.fields, { invalid: true })
    },

    placeholderMessage() {
      return `Hi, I'm interested in ${this.project.title}.`
    },

    /**
     * Remap project lead sources (name and id) to registration form
     * options (label and value). Ordered alphabetically.
     */
    defaultSourceOptions() {
      return _flow([
        (leadSources) =>
          leadSources.sort((a, b) => a.name.localeCompare(b.name)),
        (leadSources) =>
          leadSources.map(({ id: value, name: label }) => ({ label, value }))
      ])(this.project.leadSources)
    },

    registrationForm() {
      return this.project.registrationForms.length > 0
        ? this.formId
          ? this.project.registrationForms.find(
              ({ id }) => `${id}` === `${this.formId}`
            ) || null
          : this.project.registrationForms.find(
              ({ name }) => name.toLowerCase() === 'default'
            ) || null
        : null
    },

    /**
     * We prefer fields from the registration form, but fallback
     * to `project.registrationFields` (deprecated).
     *
     * We also take the opportunity to replace the deprecated `agent` field
     * with `isAgent`.
     *
     * Lastly, we ensure that the field names are unique and make sure
     * that the default fields (name, email, text) are always included.
     */
    registrationFields() {
      const explicitFields = _flow([
        (registrationForm) =>
          registrationForm
            ? registrationForm.fields
            : this.project.registrationFields,
        (registrationFields) => {
          // Replace the deprecated `agent` field with `isAgent`.
          // If `isAgent` is already set, we simply ignore the `agent` field.
          // If `isAgent` is not set, we rename `agent` to `isAgent`.

          return registrationFields.reduce(
            (acc, field) =>
              field.name === 'agent'
                ? (() => {
                    const fieldNames = registrationFields.map(
                      ({ name }) => name
                    )

                    return fieldNames.includes('isAgent')
                      ? acc
                      : [...acc, { ...field, name: 'isAgent' }]
                  })()
                : [...acc, { ...field }],
            []
          )
        }
      ])(this.registrationForm)

      const fieldNames = _flow([
        () => ['name', 'email'].concat(explicitFields.map(({ name }) => name)),
        (fieldNames) =>
          fieldNames.includes('text') ? fieldNames : fieldNames.concat('text'),
        (fieldNames) => [...new Set(fieldNames)]
      ])()

      return fieldNames.map(
        (fieldName) =>
          explicitFields.find(({ name }) => name === fieldName) ||
          DEFAULT_FORM_FIELDS.find(({ name }) => name === fieldName)
      )
    },

    dynamicallyRenderedRegistrationFields() {
      return this.registrationFields.filter(
        ({ name }) => !name.startsWith('agent[')
      )
    },

    registrationFieldNames() {
      return this.registrationFields.map(({ name }) => name)
    },

    metadataFields() {
      return this.registrationFields.filter(({ name }) =>
        name.startsWith('metadata')
      )
    },

    customFields() {
      return this.registrationFields.filter(({ name }) =>
        name.startsWith('customFields')
      )
    },

    isDomestic() {
      const countryCode = this.form?.['address[countryCode]']
      return !countryCode || countryCode === 'US'
    },

    /**
     * Cast form to their respective data types.
     * Registration forms do not yet support explicit data types,
     * so we infer from the value.
     * "true" | "false" will be cast to boolean.
     * "" will be cast to `null`.
     */
    castedForm() {
      return _reduce(
        this.form,
        (acc, v, k) => {
          const castedValue = ['true', 'false'].includes(v)
            ? v === 'true'
            : v === ''
            ? null
            : v

          return Object.assign(acc, { [k]: castedValue })
        },
        {}
      )
    },

    /**
     * Expands the flattened form.
     * qs logic casts everything to a string; which will lead to invalid
     * GQL queries. We use the decoder option to recast the values back
     * into the originally casted form values.
     */
    expandedForm() {
      /**
       * The decoder fn only gives us the key or value (not both as the same time),
       * so we temporarily bake the key into the value to make recasting possible.
       */
      const castedForm = _omit(this.castedForm, 'agent')
      const kvForm = _reduce(
        castedForm,
        (acc, v, k) => {
          return Object.assign(acc, { [k]: `${k}|${v}` })
        },
        {}
      )

      return qs.parse(qs.stringify(kvForm, { encode: false }), {
        decoder: (str, defaultDecoder, charset, type) => {
          if (type === 'value') {
            const [k, _stringValue] = str.split('|')
            return this.castedForm[k]
          } else {
            return defaultDecoder(str, defaultDecoder, charset)
          }
        }
      })
    }
  },

  watch: {
    'form.isAgent': {
      handler(isAgent) {
        this.form.isRepresented = isAgent ? false : null
      }
    }
  },

  /**
   * Lifecycle Handler: created
   */
  async created() {
    if (this.debug) {
      console.log(`[ProjectContactForm] created (Project:${this.projectId})`)
      console.log('[ProjectContactForm] $edgewise', this.$edgewise)
    }

    this.assignDefaultData()
    this.assignUtmSessionData()
    this.parseLocationSearch(window.location.search)

    try {
      await this.fetch({ projectId: this.projectId })

      if (this.debug) {
        console.log(
          `[ProjectContactForm] registration fields`,
          this.project.registrationFields
        )
        console.log(
          `[ProjectContactForm] registration forms`,
          this.project.registrationForms
        )
        console.log(`[ProjectContactForm] form`, this.form)
        console.log(
          `[ProjectContactForm] confirmation disabled: ${this.confirmationDisabled}`
        )
      }

      this.$root.$emit('created', {})
      this.$emit('created', {})
    } catch (err) {
      console.error(err)
      this.error = 'Error loading project.'
    }
  },

  /**
   * Lifecycle Handler: mounted
   */
  async mounted() {
    if (this.debug) {
      console.log(`[ProjectContactForm] mounted (Project:${this.projectId})`)
    }

    this.$root.$emit('mounted', {})
    this.$emit('mounted', {})
    this.mounted = true

    if (this.recaptchaSiteKey && window.grecaptcha) {
      window.grecaptcha.ready(() => (this.recaptchaReady = true))
    }
  },

  methods: {
    fieldClasses(fieldName) {
      const sanitizedFieldName = this.sanitizeFieldName(fieldName)

      const veeValidateClasses = this.fields[fieldName]
        ? Object.keys(this.fields[fieldName]).reduce((acc, k) => {
            return this.fields[fieldName][k]
              ? [...acc, `form-field--${k}`]
              : acc
          }, [])
        : []

      return [`form-field--${sanitizedFieldName}`].concat(veeValidateClasses)
    },

    /**
     */
    fieldLabel(fieldName, explicitDefaultLabel) {
      const fallbackLabels = {
        name: 'Name',
        email: 'Email address',
        phone: 'Phone number',
        text: 'Message',
        'address[postalCode]': 'ZIP',
        source: 'How did you hear about us?',
        preferredContactMethod: 'Preferred contact method',
        'preferences[occupancyType]': 'Occupancy type',
        'preferences[timeframe]': 'Timeframe',
        'preferences[bedrooms]': 'Bedroom preference',
        'preferences[maxPrice]': 'Max price',
        'preferences[units]': 'Unit Preferences',
        'preferences[floorPlans]': 'Floor Plan Preferences',
        'preferences[collections]': 'Collection Preferences'
      }

      const field = this.findRegistrationField(fieldName)
      const label = field
        ? field?.label ||
          explicitDefaultLabel ||
          fallbackLabels[fieldName] ||
          fieldName
        : explicitDefaultLabel || fallbackLabels[fieldName] || fieldName

      return `${label} ${this.isFieldRequired(fieldName) ? '*' : ''}`
    },

    /**
     */
    fieldOptions(fieldName) {
      const field = this.findRegistrationField(fieldName)

      const defaultOptions = {
        preferredContactMethod: DEFAULT_CONTACT_METHOD_OPTIONS,
        source: this.defaultSourceOptions,
        'address[administrativeArea]': this.states.map((state) => ({
          label: state.name,
          value: state.abbr
        })),
        'address[countryCode]': this.countries.map((country) => ({
          label: country.name,
          value: country.code
        })),
        'preferences[occupancyType]':
          this.defaultOccupancyTypePreferenceOptions,
        'preferences[timeframe]': this.defaultTimeframePreferenceOptions,
        'preferences[bedrooms]': this.defaultBedroomPreferenceOptions,
        'preferences[maxPrice]': this.defaultMaxPricePreferenceOptions
      }

      return field
        ? field.options.length > 0
          ? field.options
          : defaultOptions[fieldName] || []
        : defaultOptions[fieldName] || []
    },

    /**
     * Returns true if the field allows multiple values.
     */
    fieldAllowsMultiple(fieldName) {
      return [
        'preferences[units]',
        'preferences[floorPlans]',
        'preferences[collections]'
      ].includes(fieldName)
    },

    /**
     * Assign `defaults` property onto the form.
     * Especially useful for fields that do not have a UI (tags, metadata, etc.).
     */
    assignDefaultData() {
      try {
        const parsedDefaults = JSON.parse(this.defaults)
        if (
          typeof parsedDefaults === 'object' &&
          parsedDefaults !== null &&
          !Array.isArray(parsedDefaults)
        ) {
          if (this.debug) {
            console.log(
              `[ProjectContactForm] default form data`,
              parsedDefaults
            )
          }

          Object.assign(this.form, {
            origin: 'PROJECT_WEBSITE',
            ...parsedDefaults
          })
        }
      } catch (err) {
        console.error(
          'Error parsing contact form default data json.',
          err,
          this.defaults
        )
      }
    },

    /**
     * Fetches UTM parameters from session storage and assigns
     * them to `form.utm.*` as well as `form.metadata.utm_*`.
     */
    assignUtmSessionData() {
      const queryString = sessionStorage.getItem('utmSessionData')
      if (queryString) {
        const sessionParams = JSON.parse(queryString)
        if (this.debug) {
          console.log('[ProjectContactForm] sessionParams', {
            ...sessionParams
          })
        }
        this.assignFormUtmData(sessionParams)
      }
    },

    /**
     * Parse the URL query string into an object.
     * We use the `qs` library instead of the built-in `URLSearchParams` as it
     * supports expanding bracket notation (eg. `foo[bar]=baz`).
     *
     * Some form fields can be set by prefixing the field name with `ewz`.
     * For example, `?ewz[assigneeId]=42` will set the assignee ID to a hidden field.;
     * which will then assign the new lead to that assignee on form submit.
     *
     * UTM parameters are automatically assigned to `form.utm.*` as well as
     * `form.metadata.utm_*`.
     */
    parseLocationSearch(locationSearch) {
      if (locationSearch) {
        const queryString = locationSearch.slice(1)
        const searchParams = new URLSearchParams(queryString)
        const searchParamKeys = Array.from(searchParams.keys())

        const urlParams = qs.parse(queryString)
        this.urlParams = urlParams

        if (this.debug) {
          console.log('[ProjectContactForm] searchParamKeys', searchParamKeys)
          console.log('[ProjectContactForm] urlParams', { ...urlParams })
        }

        const hasUtm = _some(Object.keys(urlParams), (key) =>
          key.startsWith('utm_')
        )

        if (hasUtm) {
          this.assignFormUtmData(urlParams)
        }

        const hasEwz = _some(searchParamKeys, (key) => key.startsWith('ewz'))

        if (hasEwz) {
          const supportedKeys = [
            'ewz[address][postalCode]',
            'ewz[assigneeId]',
            'ewz[email]',
            'ewz[isAgent]',
            'ewz[name]',
            'ewz[phone]',
            'ewz[origin]',
            'ewz[source]',
            'ewz[secondarySourceId]',
            'ewz[tags]'
          ]

          const ewzParams = _reduce(
            searchParamKeys,
            (acc, keyPath) => {
              return supportedKeys.includes(keyPath)
                ? (() => {
                    const value = searchParams.get(keyPath)
                    const strippedKeyPath = keyPath
                      .replace(/^(ewz\[)/, '')
                      .replace(/\]/, '')
                    return Object.assign(acc, { [strippedKeyPath]: value })
                  })()
                : (() => {
                    console.warn(
                      `${keyPath} is not a supported Edgewise field.`
                    )
                    return acc
                  })()
            },
            {}
          )

          // `secondarySourceId` is deprecated.
          // If key exists and `source` does not, we assign `secondarySourceId`
          // to `source`. If `source` does exist, we simply delete `secondarySourceId`.
          /* eslint-disable no-prototype-builtins */

          if (
            Object.hasOwnProperty(ewzParams, 'secondarySourceId') &&
            !Object.hasOwnProperty(ewzParams, 'source')
          ) {
            ewzParams.source = ewzParams.secondarySourceId
          }

          if (Object.hasOwnProperty(ewzParams, 'secondarySourceId')) {
            delete ewzParams.secondarySourceId
          }

          if (this.debug) {
            console.log('[ProjectContactForm] ewzParams', { ewzParams })
          }

          Object.assign(this.form, ewzParams)
        }
      }
    },

    /**
     * Assigns UTM parameters to `form.utm.*` as well as `form.metadata.utm_*`.
     *
     * Deletes any stale UTM parameters in `form.metadata.utm_*`
     * assigned from session storage.
     */
    assignFormUtmData(urlParams) {
      if (this.form.metadata) {
        const staleUtmKeys = Object.keys(this.form.metadata).filter((key) =>
          key.startsWith('utm_')
        )
        if (staleUtmKeys.length > 0) {
          staleUtmKeys.forEach((key) => delete this.form.metadata[key])
        }
      } else {
        this.form.metadata = {}
      }

      this.form.utm = {}

      // UTM Source
      if (urlParams.utm_source) {
        this.form.utm.source = urlParams.utm_source
        this.form.metadata.utm_source = urlParams.utm_source
      }

      // UTM Medium
      if (urlParams.utm_medium) {
        this.form.utm.medium = urlParams.utm_medium
        this.form.metadata.utm_medium = urlParams.utm_medium
      }

      // UTM Campaign
      if (urlParams.utm_campaign) {
        this.form.utm.campaign = urlParams.utm_campaign
        this.form.metadata.utm_campaign = urlParams.utm_campaign
      }

      // UTM Content
      if (urlParams.utm_content) {
        this.form.utm.content = urlParams.utm_content
        this.form.metadata.utm_content = urlParams.utm_content
      }

      // UTM Term
      if (urlParams.utm_term) {
        this.form.utm.term = urlParams.utm_term
        this.form.metadata.utm_term = urlParams.utm_term
      }
    },

    /**
     *
     */
    findRegistrationField(fieldName) {
      return (
        this.registrationFields.find(({ name }) => name === fieldName) || null
      )
    },

    /**
     *
     */
    fieldDisclaimer(fieldName) {
      const field = this.findRegistrationField(fieldName)
      return field ? field.disclaimer : null
    },

    /**
     *
     */
    isFieldRequired(fieldName) {
      const field = this.findRegistrationField(fieldName)
      return field ? field.required : false
    },

    /**
     * Replace brackets (address, preferences, customFields, etc.)
     * with a preceding hyphen.
     */
    sanitizeFieldName(fieldName) {
      return fieldName ? fieldName.replace(/\[(.*)\]/g, '-$1') : fieldName
    },

    /**
     *
     */
    async fetch({ projectId }) {
      const { project } = await this.$edgewise.graphqlClient.request(
        gql`
          query ProjectContactFormComponent($projectId: ID!) {
            project(id: $projectId) {
              id
              title
              slug
              salesOffice {
                phone
              }
              leadSources {
                id
                name
              }
              registrationFields {
                name
                required
                label
                options {
                  label
                  value
                }
              }
              registrationForms {
                id
                name
                confirmationText
                fields {
                  name
                  label
                  disclaimer
                  required
                  options {
                    label
                    value
                  }
                }
              }
            }
          }
        `,
        { projectId }
      )

      this.project = project
    },

    isEmpty(value) {
      return value ? value.length === 0 : true
    },

    /**
     * Handle form submission.
     * Even though `text` (message / comments) is required by the API (for now),
     * we allow a default value of `N/A` if blank.
     */
    async onFormSubmit() {
      if (await this.$validator.validateAll()) {
        this.processing = true
      } else {
        return
      }

      var recaptcha = null
      if (this.recaptchaSiteKey && this.recaptchaReady) {
        recaptcha = await this.executeAndVerifyRecaptcha({
          siteKey: this.recaptchaSiteKey
        })

        if (this.debug) {
          console.log('[ProjectContactForm] recaptcha response', {
            recaptcha
          })
        }

        if (recaptcha.score < this.recaptchaThreshold) {
          alert('Failed to submit (suspected bot).')
          return
        }
      } else {
        if (this.debug) {
          console.log('[ProjectContactForm] skipping recaptcha.')
        }
      }

      try {
        const metadata = this.expandedForm.metadata || {}

        if (recaptcha) {
          metadata['recaptcha_score'] = recaptcha.score
        }

        const input = {
          projectId: this.project.id,
          ...this.expandedForm,
          text: this.formatFormText(
            this.expandedForm.text,
            this.metadataFields
          ),
          metadata: JSON.stringify(metadata),
          customFields: _map(this.expandedForm.customFields, (value, key) => ({
            customFieldDefinitionId: key,
            value
          }))
        }

        await this.contactProject(input)

        if (!this.confirmationDisabled) {
          const defaultPopupText = `Thank you. A representative for ${this.project.title} will respond shortly.`
          this.popupText =
            this.registrationForm?.confirmationText || defaultPopupText
          this.$nextTick(() => this.$refs.confirmButton.$el.focus())
        }

        setTimeout(this.resetForm, 250)

        this.$root.$emit('submit', { input })
        this.$emit('submit', { input })
      } catch (err) {
        console.error('[ProjectContactForm]', err)
        const formattedPhone = this.$options.filters.phone(
          this.project.salesOffice.phone
        )
        this.popupText = `We apologize, but there was an error attempting to send your message. You can reach the sales office directly at ${formattedPhone}.`
      }

      this.processing = false
    },

    /**
     *
     */
    async executeAndVerifyRecaptcha({ siteKey }) {
      if (this.debug) {
        console.log('[ProjectContactForm] execute and verify Recaptcha')
      }

      return await window.grecaptcha
        .execute(siteKey, { action: 'submit' })
        .then(async (token) => {
          const data = await this.$edgewise.graphqlClient.request(
            gql`
              mutation ComponentsExecuteRecaptcha(
                $input: VerifyRecaptchaInput!
              ) {
                verifyRecaptcha(input: $input) {
                  action
                  challengeTs
                  errorCodes
                  hostname
                  score
                  success
                }
              }
            `,
            { input: { token } }
          )

          return data.verifyRecaptcha
        })
    },

    /**
     *
     */
    async contactProject(input) {
      if (this.debug) {
        console.log('[ProjectContactForm] submission', input)
      }

      if (input.email === 'test@test.com') {
        console.log('[ProjectContactForm] test submission', input)
        return {}
      } else if (input.email === 'error@test.com') {
        throw 'Intentional Oops.'
      } else {
        const data = await this.$edgewise.graphqlClient.request(
          gql`
            mutation ComponentsContactProject($input: ContactProjectInput!) {
              message: contactProject(input: $input) {
                id
              }
            }
          `,
          { input }
        )

        return data
      }
    },

    /**
     *
     */
    resetForm() {
      this.assignDefaultData()
      this.$nextTick(() => this.$validator.reset())
    },

    /**
     * Include metadata fields in the registration form text body as `label: value` pairs
     */
    formatFormText(commentText, metadataFields) {
      if (metadataFields.length > 0) {
        const metadataText = []
        this.metadataFields.map(({ name }) => {
          const metadataLabel = this.fieldLabel(name, name)
          const metadataValue = this.form[name] || 'N/A'
          metadataText.push(`${metadataLabel}: ${metadataValue}`)
        })
        if (commentText == null) {
          return metadataText.join('\n')
        } else {
          return commentText.concat('\n', metadataText.join('\n'))
        }
      } else {
        return commentText || 'N/A'
      }
    }
  }
}
</script>

<style lang="scss">
@import '~@/assets/scss/base.scss';

.ewc-project-contact-form {
  .alert {
    .dialog {
      width: 550px;
      max-width: 100vw;
      max-height: 100vh;

      &__body {
        @include font-size(0);
        @include line-height(1);
        padding: blh(2.5) blh(2) blh(2);
        background-color: white;
        color: $gray800;
        text-align: center;

        .close-btn {
          top: blh(1);
          right: blh(1);
        }
      }

      &__footer {
        justify-content: center;
      }
    }
  }

  .form-field__disclaimer {
    @include font-size(-2);
    @include line-height(0.8);
    color: $gray600;
    font-style: italic;
    margin-top: blh(0.25);
    padding: 0px blh(0.5);
  }

  .ewr-select {
    width: 100%;
  }

  .form-field--inline {
    @include bpXs {
      flex-wrap: wrap;
      width: 100%;
    }
  }

  .form-field--inline input[type='checkbox'] {
    ~ label {
      background: none;
      padding-right: blh(2);
    }
  }

  .comments-field {
    &:nth-child(odd) {
      grid-column: 1 / -1;

      .ewr-textarea,
      [contenteditable] {
        min-height: blh(4);
      }
    }
  }

  &--columns-2 {
    .form-fields {
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-gap: blh(1);

      @include bpXs {
        grid-template-columns: 1fr;
        grid-gap: blh(0.5);
      }

      .form-field {
        margin: 0px;

        &--inline {
          &.form-field--has-disclaimer {
            grid-column: 1 / -1;
          }
        }
      }

      .ewr-textarea {
        min-height: blh(2.5);
      }
    }

    .form-actions {
      margin-top: blh(1);
    }
  }

  &--theme-edgewise {
    @include edgewiseThemeBase;
  }
}
</style>
